import { useLearnCardSurface } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import {
  CollapsiblePersistentKeyboard,
  persistentKeyboardFocus,
  useCollapsibleKeyboardUnmount,
} from "@-/dev.improve.haniverse.components.collapsible-persistent-keyboard";
import {
  areAnswerInputStatesEqual,
  LearnLayoutContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import {
  onKeyPress,
  setText as setPersistentKeyboardText,
} from "@-/dev.improve.haniverse.modules.persistent-keyboard";
import { DrillPageContext } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { useDrillPageAnswerMode } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.answer-mode";
import { useFastForwardRateAsEasy } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.fast-forward";
import { useRevealMutation } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.reveal";
import {
  useIsCorrectAnswer,
  useSubmitAnswer,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.submit";
import { cn } from "@-/com.npmjs.cn";
import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { tinykeys } from "@-/com.npmjs.tinykeys";
import useLatest from "@-/com.npmjs.use-latest";
import { useEffect } from "react";
import { useRouteBodyRef } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import { Platform, View } from "@-/com.npmjs.react-native";
import { useTheme } from "@-/dev.improve.haniverse.lib.theme";
import { safeRequestAnimationFrame } from "@-/dev.improve.haniverse.lib.scheduler";

export function DrillFooterAnswerInputbox() {
  const { isActive } = useLearnCardSurface();
  const submitAnswerMutation = useSubmitAnswer();
  const revealMutation = useRevealMutation();
  const fastForwardMutation = useFastForwardRateAsEasy();
  const [setAnswerInputState, validationState, confirmedAnswer] = useContextSelectors(
    LearnLayoutContext,
    (c) => c.setAnswerInputState,
    (c) => c.answerInputState.validationState,
    (c) => c.answerInputState.answer,
  );
  const [isAnswerModeOpen] = useContextSelectors(DrillPageContext, (c) => c.isAnswerModeOpen);
  const { openAnswerMode } = useDrillPageAnswerMode();
  const isCorrectAnswer = useIsCorrectAnswer();

  const [keyboardText] = useContextSelectors(DrillPageContext, (c) => c.keyboardText);
  const [value, _setValue, mostRecentEventCount] = keyboardText;
  const onUnmount = useCollapsibleKeyboardUnmount();
  const colorScheme = useTheme();

  const isCorrect = validationState === "correct";
  const isIncorrect = validationState === "incorrect";

  useEffect(() => {
    if (!isActive) {
      return;
    }
    setAnswerInputState((prev) => {
      const next = {
        ...prev,
        input: value,
        validationState: value !== prev.input ? null : prev.validationState,
      };
      return areAnswerInputStatesEqual(prev, next) ? prev : next;
    });
  }, [isActive, value, setAnswerInputState]);

  const isFocused = useIsFocused();
  const fastForwardMutationRef = useLatest(fastForwardMutation);
  const onSubmitAnswerRef = useLatest(() => {
    submitAnswerMutation.mutate();
  });
  const onForgotShortcutRef = useLatest(() => {
    setAnswerInputState((prev) => ({
      ...prev,
      answer: "",
      input: "",
      presetRating: 1,
      validationState: null,
    }));
    revealMutation.mutate();
  });
  const valueRef = useLatest(value);
  const isFocusedRef = useLatest(isFocused && isActive);
  const openAnswerModeRef = useLatest(openAnswerMode);
  const isAnswerModeOpenRef = useLatest(isAnswerModeOpen);

  // Rate Easy as soon as the typed input matches a correct answer — the user
  // shouldn't need to press Enter, reveal, then press Easy when they nailed it.
  // The card swipes out to the right exactly as if Easy had been pressed; the
  // deck checkpoints the answer face first, so Back still returns to it.
  // Guard against re-firing on incidental re-renders (e.g., when the
  // expected-answer-set memo updates upstream while the matching value is
  // still on screen) so we submit at most once per typed match.
  // Flip validationState to "correct" and preselect Easy in the same tick so
  // the green UI paints while the card swipes away.
  const lastAutoSubmittedValueRef = useRouteBodyRef<string | null>("meaning-auto-submitted", null);
  useEffect(() => {
    if (!isActive) {
      return;
    }
    if (value.length === 0) {
      lastAutoSubmittedValueRef.current = null;
      return;
    }
    if (
      lastAutoSubmittedValueRef.current === value ||
      (validationState === "correct" && confirmedAnswer === value)
    ) {
      return;
    }
    if (!isCorrectAnswer(value)) {
      return;
    }
    lastAutoSubmittedValueRef.current = value;
    setAnswerInputState((prev) => ({
      ...prev,
      answer: value,
      input: value,
      presetRating: 4,
      validationState: "correct",
    }));
    fastForwardMutationRef.current.mutate();
  }, [
    confirmedAnswer,
    isActive,
    fastForwardMutationRef,
    isCorrectAnswer,
    lastAutoSubmittedValueRef,
    setAnswerInputState,
    value,
    validationState,
  ]);

  useEffect(() => {
    const sub = onKeyPress((event) => {
      if (!isFocusedRef.current) {
        return;
      }
      if (event.key === "Enter") {
        onSubmitAnswerRef.current();
        return;
      }
      if (event.key === " " && valueRef.current.length === 0) {
        // Spacebar on an empty input is the "I forgot" shortcut: reveal with Again preselected.
        setPersistentKeyboardText("");
        onForgotShortcutRef.current();
      }
    });
    return () => {
      sub.remove();
    };
  }, [isFocusedRef, onForgotShortcutRef, onSubmitAnswerRef, valueRef]);

  useEffect(() => {
    if (Platform.OS !== "web") {
      return;
    }
    return tinykeys(
      globalThis.window,
      {
        "[Shift]+Slash": (event) => {
          if (!isFocusedRef.current) {
            return;
          }
          event.preventDefault();
          if (!isAnswerModeOpenRef.current) {
            openAnswerModeRef.current();
          }
          // Defer focus until after answer-mode UI (reveal button, dismiss slot) commits.
          safeRequestAnimationFrame(() => {
            persistentKeyboardFocus();
          }, "drill-answer-shortcut-focus");
        },
      },
      { capture: true },
    );
  }, [isAnswerModeOpenRef, isFocusedRef, openAnswerModeRef]);

  const defaultTextColor = colorScheme === "dark" ? "#f9fafb" : "#111827";
  const correctTextColor = colorScheme === "dark" ? "#4ade80" : "#16a34a";
  const textColor = isCorrect ? correctTextColor : defaultTextColor;
  const placeholderColor =
    colorScheme === "dark" ? "rgba(249, 250, 251, 0.5)" : "rgba(17, 24, 39, 0.5)";

  return (
    <View
      className={cn(
        "h-full flex-1 flex-row items-center px-3",
        isCorrect &&
          "rounded-xs border border-green-600 bg-green-600/10 dark:border-green-400 dark:bg-green-400/16",
        isIncorrect &&
          "rounded-xs border border-red-600 bg-red-600/10 dark:border-red-400 dark:bg-red-400/16",
      )}
    >
      <CollapsiblePersistentKeyboard
        color={textColor}
        fontFamily="Han Composite"
        fontSize={17}
        mostRecentEventCount={mostRecentEventCount}
        onBlur={() => true}
        onUnmount={onUnmount}
        placeholder="Or, type your answer here..."
        placeholderTextColor={placeholderColor}
        text={value}
        textPaddingLeft={0}
      />
    </View>
  );
}
